import { Image } from 'antd';
import { AxiosResponse } from 'axios';
import { CreateShipmentResponse, Rate } from '../../../../apis/client-axios';
import { SvgExclamationPoint } from '../../../../components/@svg/SvgExclamationPoint';
import ButtonAgree from '../../../../components/buttonCustom/ButtonAgree';
import ButtonCancel from '../../../../components/buttonCustom/ButtonCancel';
import { TDataRequest } from '../request';
import { AUTO_CHEAPEST_CARRIER } from './Carrier';

const CARRIER_WARNING_TEXT = `送料は概算での仮請求となります。
実際の同梱サイズ・重量確定後に送料を再計算し、差額が生じた場合は返金または追加でのご請求となります。`;
const CONFIRM_WARNING_TEXT = `こちらの金額が仮決済されます。
  実際の同梱サイズ・重量確定後、差額が生じた場合は返金または追加でのご請求となります。`;
// Đơn lẻ item CHƯA lưu kho (chưa có 保管サイズ, phí tạm theo 到着時サイズ) — văn án khách chốt 2026-09-10.
const UNSTORED_SIZE_WARNING_TEXT = `梱包サイズ・重量が未確定のため、送料は概算での仮請求となります。
実際のサイズ・重量確定後に送料を再計算し、差額が生じた場合は返金または追加でのご請求となります。`;

type TProps = {
  dataRequest: TDataRequest;
  handleClickBack: () => void;
  handleSubmit: () => void;
  carrier?: AxiosResponse<CreateShipmentResponse, any> | Rate[];
  orderByCountry?: string;
  requireCarrier?: boolean;
  showEmptyStateWhenNoCarrier?: boolean;
  disableSubmitWithoutCarrier?: boolean;
  disableSubmitByFormState?: boolean;
  showCarrierWarningAlways?: boolean;
  showConfirmWarning?: boolean;
  showCarrierWarning?: boolean;
  // Đơn lẻ item chưa lưu kho: hiện cảnh báo 仮請求 theo UNSTORED_SIZE_WARNING_TEXT (cả trước và sau confirm).
  showUnstoredSizeWarning?: boolean;
  isMultiShip?: boolean;
  itemCount?: number;
  submitting?: boolean;
};

const Bill = ({
  dataRequest,
  handleClickBack,
  handleSubmit,
  carrier,
  orderByCountry,
  requireCarrier = true,
  showEmptyStateWhenNoCarrier = false,
  disableSubmitWithoutCarrier = false,
  disableSubmitByFormState = false,
  showCarrierWarningAlways = false,
  showConfirmWarning = false,
  showCarrierWarning = true,
  showUnstoredSizeWarning = false,
  submitting = false,
}: TProps) => {
  const isUS = orderByCountry === 'US';
  const hasCarrierSelected = !!dataRequest?.carrierPrice;
  const isMissingFields = !isUS && (!dataRequest.HSCode || dataRequest.itemPrice <= 0);
  const showInsuranceOnlyState = showEmptyStateWhenNoCarrier && !hasCarrierSelected && dataRequest.isInsurance;
  const disabled =
    disableSubmitByFormState &&
    ((disableSubmitWithoutCarrier && requireCarrier && !dataRequest?.carrierPrice) || isMissingFields);
  const showEmptyCarrierState = showEmptyStateWhenNoCarrier && !hasCarrierSelected && !showInsuranceOnlyState;
  const shouldShowCarrierWarning =
    showCarrierWarning && !dataRequest.confirm && (showEmptyCarrierState || showCarrierWarningAlways);
  const carrierRates = Array.isArray(carrier) ? carrier : (Object.values(carrier?.data?.rates || {}) as Rate[]);
  const selectedCarrierRates = carrierRates.filter((v: Rate) =>
    dataRequest.confirm ? v?.provider === dataRequest.carrier : true,
  );
  const isAutoCheapest = dataRequest.carrier === AUTO_CHEAPEST_CARRIER;
  const shippingPrice = Number(dataRequest?.carrierPrice || 0);

  const totalPrice = shippingPrice + (dataRequest.isInsurance ? dataRequest.insurance : 0);

  return (
    <div className="bill">
      {showEmptyCarrierState ? (
        <div className="bill__empty">
          <div className="bill__empty-space" />
          {shouldShowCarrierWarning && (
            <div className="bill__warning">
              <SvgExclamationPoint />
              <span className="bill__warning-text" style={{ whiteSpace: 'pre-line' }}>
                {CARRIER_WARNING_TEXT}
              </span>
            </div>
          )}
        </div>
      ) : !dataRequest.confirm ? (
        <div className="bill__info">
          <div className="bill__info__item">
            <span className="label">送料</span>
            <span className="value">${shippingPrice.toFixed(2)}</span>
          </div>
          <div className="bill__info__item">
            <span className="label">保険料</span>
            <span className="value">${(dataRequest.isInsurance ? dataRequest.insurance : 0).toFixed(2)}</span>
          </div>
          {shouldShowCarrierWarning && (
            <div className="bill__warning">
              <SvgExclamationPoint />
              <span className="bill__warning-text" style={{ whiteSpace: 'pre-line' }}>
                {CARRIER_WARNING_TEXT}
              </span>
            </div>
          )}
        </div>
      ) : dataRequest.confirm ? (
        <div className="bill__confirm">
          <div className="bill__confirm__item">
            <span className="title">送料</span>
            {hasCarrierSelected && (
              <div className="carrier">
                {isAutoCheapest ? (
                  <div className="carrier-left" key="auto-cheapest">
                    <Image preview={false} width={64} src="/assets/icons/carrier/shipping_carrier.png" />
                    <div className="carrier-left__info">
                      <span className="name">最安を自動選択</span>
                    </div>
                  </div>
                ) : selectedCarrierRates.length > 0 ? (
                  selectedCarrierRates.map((item) => (
                    <div className="carrier-left" key={item?.objectId}>
                      <Image
                        preview={false}
                        width={64}
                        src={`/assets/icons/carrier/${item?.provider?.toLowerCase()}.png`}
                      />
                      <div className="carrier-left__info">
                        <span className="name">
                          {((item?.servicelevel as any)?.displayName ?? (item?.servicelevel as any)?.name)
                            ?.replace(/®/g, '')
                            .trim()}
                        </span>
                        <span className="day">About {item?.estimatedDays} days</span>
                      </div>
                    </div>
                  ))
                ) : (
                  dataRequest.confirm &&
                  dataRequest.carrier && (
                    <div
                      className="carrier-left"
                      key={`${dataRequest.carrier}-${dataRequest.shippingRate || 'fallback'}`}
                    >
                      <Image
                        preview={false}
                        width={64}
                        src={`/assets/icons/carrier/${dataRequest.carrier.toLowerCase()}.png`}
                      />
                      <div className="carrier-left__info">
                        <span className="name">{dataRequest.shippingPlan || dataRequest.carrier}</span>
                      </div>
                    </div>
                  )
                )}
                <div className="carrier-right">
                  <span className="price">${shippingPrice.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
          <div className="bill__confirm__item">
            <span className="title">オプション</span>
            <div className="insurance">
              <span className="label">保険料</span>
              <span className="value">${(dataRequest.isInsurance ? dataRequest.insurance : 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bill__info">
          <div className="bill__info__item">
            <span className="label">送料</span>
            <span className="value">${shippingPrice.toFixed(2)}</span>
          </div>
          <div className="bill__info__item">
            <span className="label">保険料</span>
            <span className="value">${(dataRequest.isInsurance ? dataRequest.insurance : 0).toFixed(2)}</span>
          </div>
        </div>
      )}
      {dataRequest.confirm && showConfirmWarning && (
        <div className="bill__warning bill__warning--confirm">
          <SvgExclamationPoint />
          <span className="bill__warning-text" style={{ whiteSpace: 'pre-line' }}>
            {CONFIRM_WARNING_TEXT}
          </span>
        </div>
      )}
      {showUnstoredSizeWarning && (
        <div className="bill__warning bill__warning--confirm">
          <SvgExclamationPoint />
          <span className="bill__warning-text" style={{ whiteSpace: 'pre-line' }}>
            {UNSTORED_SIZE_WARNING_TEXT}
          </span>
        </div>
      )}
      <div className="bill__total">
        <div className="bill__total__item">
          <span className="label">合計金額</span>
          <span className="value">${totalPrice.toFixed(2)}</span>
        </div>
        <div className="request-ship-2-buyer-button-wrap">
          <ButtonCancel value="戻る" onClick={handleClickBack} />
          <ButtonAgree
            value={dataRequest.confirm ? '確定' : '確認'}
            onClick={handleSubmit}
            disabled={disabled || submitting}
          />
        </div>
      </div>
    </div>
  );
};

export default Bill;
